import React, { useState } from 'react';
import {
  Box,
  Grid,
  styled,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Group } from '@mui/icons-material';
import { useFeatureFlagEnabled } from 'posthog-js/react';
import '../../../components/EventParticipantTable.scss';
import TablePagination from '@mui/material/TablePagination';
import { useLanguage } from '../../../../common/GeneralUtils';
import RotatingLoader from '../../../../common/ui/RotatingLoader';
import { CustomPersonalDetailData, EnrichedEventData, OrderData } from '../../../../api';
import { useEventOrders } from '../../../swr/useEventOrders';
import { orderMatchesSearch } from '../../../CalendarUtils';
import { buildParticipantsExportColumns } from './participantsExportColumns';
import { ExportButton } from '../../../../common/export/ExportButton';
import { exportRows } from '../../../../common/export/exportTable';
import EventParticipantsRow from './EventParticipantsRow';
import { UIObjectTypes } from '../../../../editOrderUiObjects/UiObjectsList';

export const StyledTable = styled(Table)(() => ({
  whiteSpace: 'pre',
  '& thead': {
    '& tr': { '& th': { paddingInline: 8 } },
  },
  '& tbody': {
    '& tr': { '& td': { paddingInlineStart: 0, textTransform: 'capitalize' } },
  },
}));

function participantsSort(a: OrderData, b: OrderData) {
  // Check if both 'a' and 'b' have 'checkin' and 'checkin.persons' equal to 'persons'
  const aMatches = a.checkin && a.checkin.persons === a.persons;
  const bMatches = b.checkin && b.checkin.persons === b.persons;

  // Sort based on the matching conditions
  if (aMatches && !bMatches) {
    return 1; // 'a' comes after 'b'
  }
  if (!aMatches && bMatches) {
    return -1; // 'a' comes before 'b'
  }
  const aName = `${a.firstname} ${a.lastname}`;
  const bName = `${b.firstname} ${b.lastname}`;
  return aName.localeCompare(bName);
}

type EventParticipantsTableProps = {
  event: EnrichedEventData;
  orderPage: any;
  setOrder: (order: OrderData) => void;
  setEditOrderIsOpen: (isOpen: boolean) => void;
  setCheckInDialogIsOpen: (isOpen: boolean) => void;
  isInEventMode: boolean;
};
const EventParticipantsTable = ({
  event,
  orderPage,
  setOrder,
  setEditOrderIsOpen,
  setCheckInDialogIsOpen,
  isInEventMode,
}: EventParticipantsTableProps) => {
  const [searchQuery, setSearchQuery] = useState('');
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));
  const { isLoading: isLoadingOrderList, items: ordersList, removeOrder } = useEventOrders({ eventId: event.id });
  // @ts-ignore
  const { t } = useTranslation();
  const language = useLanguage();
  const pickupFeatureEnabled = useFeatureFlagEnabled('feature-pickup-ready');
  const filteredOrdersList = ordersList.filter((order) => orderMatchesSearch(order, searchQuery));

  // Guest/member pairing. A guest order carries member_order_id; a member order carries
  // guest_order_id. A guest whose member isn't in this same session simply renders as a regular
  // row.
  const orderById = new Map(ordersList.map((o) => [o.id, o]));
  const isGuestOrder = (o: OrderData) => !!o.member_order_id;

  const sortedOrders = [...filteredOrdersList].sort(participantsSort);
  const groupedOrders: OrderData[] = [];
  sortedOrders.forEach((o) => {
    if (isGuestOrder(o)) return; // inserted right after its member below
    groupedOrders.push(o);
    if (o.guest_order_id) {
      const guest = orderById.get(o.guest_order_id);
      if (guest) groupedOrders.push(guest);
    }
  });

  // Added pagination here as front finds it hard to show more then few 10s of lines and starts hanging
  const [page, setPage] = useState(0);
  const rowsPerPage = 25;
  const shouldEnablePagination = groupedOrders.length > 40;
  const handlePageChange = (_: any, newPage: number) => {
    setPage(newPage);
  };
  if (isLoadingOrderList) {
    return (
      <Box
        sx={{
          minHeight: '200px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
        <RotatingLoader />
      </Box>
    );
  }
  return (
    <Grid
      container
      flexDirection="column"
      flexWrap="nowrap"
      mt={1}
      p={1}
      border="1px solid"
      borderColor="divider"
      borderRadius="8px"
      sx={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
      <Grid container mb={2} justifyContent="space-between" sx={{ flexShrink: 0 }}>
        <Grid container gap={2} xs={7}>
          <Grid item maxWidth="350px">
            <TextField
              placeholder={t('orders.searchParticipantsPlaceHolder')}
              size="small"
              variant="outlined"
              value={searchQuery}
              onChange={(e: any) => setSearchQuery(e.target.value)}
              fullWidth
            />
          </Grid>
        </Grid>

        <Grid item>
          <ExportButton
            label={t('export.downloadParticipants')}
            onExport={(format) =>
              exportRows({
                columns: buildParticipantsExportColumns(
                  orderPage.custom_personal_details,
                  orderPage.custom_details,
                  language,
                  t,
                ),
                rows: filteredOrdersList,
                fileName: `Orders-${event.date}`,
                format,
                language,
                sheetName: t('export.downloadParticipants'),
              })
            }
          />
        </Grid>
      </Grid>
      <Grid
        container
        flexDirection="column"
        flexWrap="nowrap"
        className="event-participant-table"
        borderRadius="4px"
        sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
        {!filteredOrdersList.length && ordersList.length ? (
          <Grid container m={4}>
            {t('orders.nonfound')}
          </Grid>
        ) : (
          <Grid container className="table-container">
            <StyledTable size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  {!isInEventMode && (
                    <>
                      <TableCell sx={{ padding: '0!important', width: '40px' }} />
                      <TableCell sx={{ padding: '0!important', width: '40px' }} />
                      <TableCell sx={{ padding: '0!important', width: '30px' }} />
                      {pickupFeatureEnabled && <TableCell sx={{ padding: '0!important', width: '30px' }} />}
                    </>
                  )}
                  <TableCell sx={{ padding: '0!important', width: '30px' }} />
                  <TableCell align="left">{t('orders.name')}</TableCell>
                  {orderPage.custom_details &&
                    orderPage.custom_details.map((custom_detail: any) => {
                      return <TableCell align="left">{custom_detail.label[language]}</TableCell>;
                    })}
                  <TableCell>{isSmallScreen ? <Group /> : t('orders.num_persons')}</TableCell>
                  {orderPage.custom_personal_details
                    .filter((x: CustomPersonalDetailData) => !x.hide_from_console)
                    .map((customDetail: CustomPersonalDetailData) => {
                      const label = customDetail.console_label || customDetail.label;
                      return <TableCell align="left">{label[language]}</TableCell>;
                    })}
                  {orderPage.objects
                    .filter((x: any) => x.type === UIObjectTypes.TOGGLE_BUTTON_GROUP)
                    .map((x: any) => {
                      const label = x.external_label;
                      return <TableCell align="left">{label[language]}</TableCell>;
                    })}
                  <TableCell align="left">{t('orders.comment')}</TableCell>
                  <TableCell align="left">{t('orders.phone')}</TableCell>
                  <TableCell align="left">{t('orders.order_number')}</TableCell>
                  <TableCell align="left">{t('orders.price')}</TableCell>

                  <TableCell align="left">{t('orders.status')}</TableCell>
                </TableRow>
              </TableHead>

              <TableBody className="table-body">
                {groupedOrders
                  .slice(
                    shouldEnablePagination ? rowsPerPage * page : 0,
                    shouldEnablePagination ? rowsPerPage * (page + 1) : groupedOrders.length,
                  )
                  .map((order, index) => {
                    const asGuest = isGuestOrder(order);
                    const asHost = !!order.guest_order_id;
                    let counterpartyName: string | undefined;
                    if (asGuest && order.member_order_id) {
                      const member = orderById.get(order.member_order_id);
                      counterpartyName = member ? `${member.firstname} ${member.lastname}` : undefined;
                    } else if (asHost && order.guest_order_id) {
                      const guest = orderById.get(order.guest_order_id);
                      counterpartyName = guest ? `${guest.firstname} ${guest.lastname}` : undefined;
                    }
                    return (
                      <EventParticipantsRow
                        order={order}
                        index={index}
                        orderPageCustomPersonalDetails={orderPage.custom_personal_details}
                        orderCustomDetails={orderPage.custom_details}
                        removeOrder={removeOrder}
                        setCheckInDialogIsOpen={setCheckInDialogIsOpen}
                        setOrder={setOrder}
                        setEditOrderIsOpen={setEditOrderIsOpen}
                        isInEventMode={isInEventMode}
                        orderPage={orderPage}
                        isGuest={asGuest}
                        hasGuest={asHost}
                        counterpartyName={counterpartyName}
                      />
                    );
                  })}
              </TableBody>
            </StyledTable>
          </Grid>
        )}
      </Grid>
      {shouldEnablePagination && (
        <Grid container justifyContent="flex-end" sx={{ flexShrink: 0 }}>
          <TablePagination
            sx={{ px: 2 }}
            page={page}
            component="div"
            rowsPerPage={rowsPerPage}
            count={groupedOrders.length}
            onPageChange={handlePageChange}
            rowsPerPageOptions={[]}
          />
        </Grid>
      )}
    </Grid>
  );
};

export default EventParticipantsTable;
